import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AddressPicker from '../../common/AddressPicker';

/**
 * Section 2 of the 5-section campaign registration layout.
 *
 * Scope: the short "제공 제품/서비스 정보" card from the reference layout —
 * product/service name, market value, and visit address with embedded
 * map. The longer-form介绍 fields (intro, provision method, visit notes,
 * attachments, photos) live in {@link ../sections/CampaignDetailV2} to
 * match the reference grouping.
 *
 * Field names (data model) are unchanged: `productName`,
 * `providedValueDescription`, `visitAddress`, `visitLatitude`,
 * `visitLongitude`.
 */
interface Props {
  control: Control<any>;
  setValue: any;
  watch: any;
}

const SUPPORTED_COUNTRY_CODES = ['kr', 'us', 'gu', 'th', 'sg', 'hk', 'id', 'vn', 'jp', 'au', 'gb'] as const;
type SupportedCountryCode = typeof SUPPORTED_COUNTRY_CODES[number];

const ProductServiceBasicV2: React.FC<Props> = ({ control, setValue, watch }) => {
  const { t } = useTranslation(['campaigns', 'common']);

  return (
    <div className="space-y-4">
      {/* 제공 제품/서비스명 */}
      <Controller
        name="productName"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.productName')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              {...field}
              rows={3}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${
                fieldState.error
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
          </div>
        )}
      />

      {/* 제품/서비스 가치 */}
      <Controller
        name="providedValueDescription"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.productValue')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              {...field}
              rows={3}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${
                fieldState.error
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
          </div>
        )}
      />

      {/* 국가 */}
      <Controller
        name="visitCountry"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.visitCountry')}{' '}
              <span className="text-red-500">*</span>
            </label>
            {/*
              Read-only: country is set automatically from the resolved
              address (Google geocoder) or the brand's location. The user
              has no way to override it directly — this keeps country in
              sync with the actual address rather than two competing
              sources of truth.
            */}
            <select
              {...field}
              value={field.value || ''}
              disabled
              aria-readonly="true"
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed ${
                fieldState.error ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">{t('common:selectCountry', { defaultValue: '국가를 선택해 주세요' })}</option>
              {SUPPORTED_COUNTRY_CODES.map((code) => (
                <option key={code} value={code}>{t(`common:countries.${code}`)}</option>
              ))}
            </select>
          </div>
        )}
      />

      {/* 도시 — read-only, populated by address picker / brand */}
      <Controller
        name="visitCity"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.visitCity')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <input
              {...field}
              type="text"
              readOnly
              aria-readonly="true"
              placeholder={t('campaigns:registration.v2.visitCityPlaceholder', { defaultValue: '' })}
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed ${
                fieldState.error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
          </div>
        )}
      />

      {/* 상세 주소 */}
      <Controller
        name="visitAddress"
        control={control}
        rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.visitAddress')}{' '}
              <span className="text-red-500">*</span>
            </label>
            <AddressPicker
              mode="single"
              value={{
                text: field.value || '',
                latitude: watch('visitLatitude') ?? null,
                longitude: watch('visitLongitude') ?? null,
              }}
              onChange={(next, extras) => {
                field.onChange(next.text);
                setValue('visitLatitude', next.latitude);
                setValue('visitLongitude', next.longitude);
                // When the user selects a Google Places suggestion the
                // picker delivers the parsed address components in
                // `extras`. Auto-fill country/city so the user doesn't
                // have to retype them. ISO-3166 alpha-2 is lowercased to
                // match our `common:countries.*` keys.
                if (extras) {
                  if (extras.country) {
                    const code = extras.country.toLowerCase() as SupportedCountryCode;
                    if ((SUPPORTED_COUNTRY_CODES as readonly string[]).includes(code)) {
                      setValue('visitCountry', code, { shouldValidate: true });
                    }
                  }
                  if (extras.city) {
                    setValue('visitCity', extras.city, { shouldValidate: true });
                  }
                }
              }}
              hasError={!!fieldState.error}
            />
          </div>
        )}
      />
    </div>
  );
};

export default ProductServiceBasicV2;
